import { Link } from 'react-router-dom';
import SEO, { type Crumb } from '@/components/SEO';
import ServiceExperience from '@/components/services/ServiceExperience';
import ContentIndex from '@/components/ContentIndex';
import FAQ from '@/components/FAQ';
import CTABand from '@/components/CTABand';
import { contentBySection, staticPage } from '@/lib/content';
import { ENGAGEMENTS, inquiryHref } from '@/lib/engagements';

// Four offers share names with the service navigation. Commercial scope is agreed.
const TIERS = [
  {
    offer: ENGAGEMENTS[3],
    shape: 'A banked block of engineer days per year',
    body: 'Patches, break-fix, small changes and advice for content and invoice workflows. Recognition exceptions, coding issues and stalled approvals go on the shared work list. Monthly statements show days used and remaining.',
    fit: 'For an estate that needs ongoing engineering capacity under agreed coverage and priorities.',
  },
  {
    offer: ENGAGEMENTS[1],
    shape: 'A defined engagement around a release change',
    body: 'Assess the supported path and dependencies, then scope implementation support, rehearsal and acceptance checks. Platform readiness and the forms, integrations and workflows users depend on each need evidence.',
    fit: 'For an estate with a release change to plan and validate.',
  },
  {
    offer: ENGAGEMENTS[2],
    shape: 'A defined move or integration change',
    body: 'Scope a hosting move, new ERP connection or replacement of a defined workload. Agree document reconciliation, application tests, workflow closeout or in-flight handling, and handover.',
    fit: 'For a change to where the process runs or how its systems connect.',
  },
  {
    offer: ENGAGEMENTS[0],
    shape: 'A one-time inspection of a WebCenter estate',
    body: 'Inspect versions, customizations and integrations, then trace recurring issues through recognition, coding, approvals and document access. The findings and roadmap set out the dependencies and order of work.',
    fit: 'For the estate that has not been looked at as a whole for a while.',
  },
];

const STEPS = [
  { n: '01', title: 'Describe the estate', body: 'Versions, integrations and the process that is failing — recognition exceptions, coding, approvals or document access. A short call or a written note works.' },
  { n: '02', title: 'Agree the scope', body: 'Choose a support retainer, upgrade, migration or Estate Review. Write down the deliverables, responsibilities and terms.' },
  { n: '03', title: 'Start the work', body: 'A named engineer, a shared ticket list, and access arranged on your terms.' },
  { n: '04', title: 'Review the work', body: 'For a retainer, monthly statements show days used, remaining work and priorities. Defined engagements use the agreed milestones and acceptance evidence.' },
];

const FAQS = [
  {
    q: 'What counts as a day on a standing retainer?',
    a: 'An engineer day of work on your estate — patching, break-fix, a small change, or written advice. Days are logged as they are used and shown on the monthly statement.',
  },
  {
    q: 'What happens to unused days?',
    a: 'The treatment of unused days is agreed as part of your retainer contract before work begins.',
  },
  {
    q: 'Can a standing retainer cover a 12c → 14c upgrade?',
    a: 'Small upgrades, sometimes. A full 12c → 14c program is better scoped as an upgrade engagement with assessment, planning, execution support and validation, so the retainer days stay available for day-to-day support.',
  },
  {
    q: 'What does the estate review produce?',
    a: 'A written findings and roadmap document covering versions, components, customizations, integrations and patch levels, plus recurring workflow issues. It recommends an order of work and explains the dependencies behind it.',
  },
  {
    q: 'How do we get access to you?',
    a: 'Access is arranged through the agreed VPN, bastion and ticketing process. Credential handling, approved tools, access duration and removal are defined for the engagement. Please keep credentials out of the public inquiry form.',
  },
  {
    q: 'What response coverage does a retainer include?',
    a: 'The scope defines coverage hours, response targets by severity, escalation contacts and available capacity. After-hours or urgent coverage is agreed explicitly. The website inquiry response expectation is separate from any contracted incident response target.',
  },
];

const Support = () => {
  const page = staticPage('/support');
  const items = contentBySection('support');
  const crumbs: Crumb[] = [
    { name: 'Home', href: '/' },
    { name: 'Support retainers', href: '/support' },
  ];
  return (
    <>
      <SEO title={page.title} description={page.description} path="/support" breadcrumbs={crumbs} />
      <ServiceExperience
        kind="support"
        crumbs={crumbs}
        label="Support retainers"
        title="A standing engineer for the estate."
        lead="Keep content and invoice workflows moving with a named engineer. Choose day-to-day support, a defined upgrade or migration, or a review of the connected estate."
        note="A named engineer. Access on your terms. Work and scope in writing."
      />

      <section id="support-shapes" aria-labelledby="tiers-heading" className="container service-section">
        <div className="service-section-heading">
          <div><p className="label">THE ENGAGEMENT</p><h2 id="tiers-heading">Support shaped<br />around the work.</h2></div>
          <p>Day-to-day ownership, a defined move or a fresh assessment. Each has a different scope and a clear way to see what has been done.</p>
        </div>
        <ul className="service-retainers service-four-offers">
          {TIERS.map((t, i) => (
            <li key={t.offer.to} className="service-retainer">
              <span className="service-retainer-index" aria-hidden="true">0{i + 1}</span>
              <p className="label">{t.offer.label}</p>
              <h3>{t.shape}</h3>
              <p className="service-retainer-body">{t.body}</p>
              <p className="service-retainer-fit">{t.fit}</p>
              <div className="service-retainer-link"><Link to={t.offer.to === '/support' ? inquiryHref('/support') : t.offer.to} className="btn-outline-forest">{t.offer.to === '/support' ? 'Discuss support coverage' : `Explore ${t.offer.label}`} <span aria-hidden="true">↗</span></Link></div>
            </li>
          ))}
        </ul>
        <p className="mt-8 max-w-4xl border-l-2 border-brass pl-5 text-body">
          Before work starts, agree coverage hours, response targets, escalation contacts and available capacity.
          Urgent and after-hours work needs an explicit arrangement; a bank of days alone does not establish round-the-clock coverage.
        </p>
      </section>

      <section aria-labelledby="how-heading" className="service-working on-forest">
        <div className="container">
          <p className="label mb-4">A WORKING RELATIONSHIP</p>
          <h2 id="how-heading">Clear scope. Visible work.</h2>
          <ol className="service-working-steps">
            {STEPS.map((s) => (
              <li key={s.n}>
                <p className="label">{s.n}</p>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section aria-labelledby="guides-heading" className="container service-section">
        <div className="service-guides-heading">
          <h2 id="guides-heading">The day-to-day detail.</h2>
          <p>Field guidance on support policy, sizing, loading and the operational work around WebCenter.</p>
        </div>
        <div>
          <ContentIndex
            items={items}
            emptyHeading="Support guides are being added."
            emptyBody="Sustaining Support vs Extended Support, sizing, batch loader and the day-2 operations pieces are being written now."
          />
        </div>
      </section>

      <section className="container pb-16 md:pb-20">
        <div className="max-w-[46rem]">
          <FAQ items={FAQS} title="Retainer questions" />
        </div>
      </section>

      <CTABand variant="engineer" />
    </>
  );
};

export default Support;
